import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import portfolio from "@/data/portfolio";

export default function MadridHorseWeek() {
  const d = portfolio.madridHorseWeek;
  return (
    <section
      id="madrid"
      data-testid="madrid-horse-week-section"
      className="py-24 md:py-40 border-t hairline bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-14">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-block w-8 h-px bg-black/40" />
              <span className="text-xs font-semibold uppercase tracking-[0.22em] text-gray-500">
                04 · {d.eyebrow}
              </span>
            </div>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-black leading-[1.05]">
              {d.title}
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-lg text-gray-700 font-light leading-relaxed">
              {d.headline}
            </p>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="relative overflow-hidden rounded-[2rem] md:rounded-[3rem] bg-[#F5F5F7]"
        >
          <img
            src={d.image}
            alt="Madrid Horse Week"
            className="w-full h-[50vh] md:h-[70vh] object-cover"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/0 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 text-white">
            <div className="text-[10px] md:text-xs font-semibold uppercase tracking-[0.22em] opacity-80">
              IFEMA · Madrid · Spain
            </div>
            <div className="mt-2 font-serif-display text-3xl md:text-5xl font-light max-w-2xl">
              Europe's premier indoor FEI World Cup.
            </div>
          </div>
        </motion.div>

        <div className="mt-12 md:mt-16 grid grid-cols-2 md:grid-cols-4 border-t hairline">
          {d.metrics.map((m, i) => (
            <motion.div
              key={m.label}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.06 }}
              data-testid={`mhw-metric-${i}`}
              className={`py-8 md:py-10 ${i > 0 ? "md:border-l hairline" : ""} ${i >= 2 ? "border-t md:border-t-0 hairline" : ""} ${(i === 1 || i === 3) ? "border-l hairline" : ""} pl-5 md:pl-6`}
            >
              <div className="font-serif-display text-4xl md:text-5xl font-light text-black tracking-tight">
                {m.value}
              </div>
              <div className="mt-3 text-sm text-gray-500">{m.label}</div>
            </motion.div>
          ))}
        </div>

        <div className="mt-12 md:mt-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-[0.22em] text-gray-500">
              Scope of leadership
            </h3>
            <div className="mt-5 flex flex-wrap gap-2">
              {d.pillars.map((p) => (
                <span
                  key={p}
                  data-testid={`mhw-pillar-${p.replace(/\s+/g, "-").toLowerCase()}`}
                  className="rounded-full bg-white border hairline px-5 py-2 text-sm text-gray-800 hover:border-black/40 transition-colors"
                >
                  {p}
                </span>
              ))}
            </div>
          </div>
          <Link
            to="/projects/madrid-horse-week"
            data-testid="mhw-open-case-study"
            className="inline-flex items-center gap-2 bg-black text-white hover:bg-[#1a1a1a] rounded-full px-6 py-3 text-sm font-medium transition-colors self-start md:self-auto"
          >
            View more <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

